import { useEffect, useMemo, useRef, useState } from 'react';
import { t } from '@app/i18n';
import {
  Alert,
  Card,
  ErrorBanner,
  Field,
  FieldGrid,
  LoadingButton,
  SearchCombobox,
  useActionSlow,
  type ComboOption,
} from '@zarya/ui';
import { useIdentities } from '@app/main/Decisions';
import { useMultiSite } from '@app/main/Sites/Sites.api';
import { Swap } from './Swap';
import { useCapabilities, useToolsCatalog, useCallDecision, useLabPeople } from './Converse.api';
import { useConversation } from './useConversation';
import { swapExecutor, toolsOferecidas, type SwapInScreen } from './ConverseModel';
import s from './Conversar.module.css';

/**
 * T25 — «Conversar com agente». Dispara uma chamada de tool **de verdade** no
 * lab, com o token de uma pessoa, e mostra por onde ela passou.
 *
 * Até 29/08 a única forma de fazer isso era `scripts/lab-chamar.sh`: um pod
 * `curlimages/curl` criado com `kubectl run` dentro do cluster, porque a
 * NetworkPolicy de egresso dos agentes só libera DNS, o `/32` do gateway e o
 * emissor do token de ação.
 * Quem apresenta a PoC não tem terminal do cluster à mão.
 *
 * ## Onde ela mora (29/08)
 *
 * Não no menu. Esta tela **não é funcionalidade do produto**: é ferramenta de
 * bastidor da demonstração, e vive em `/console-de-demo`, dentro da moldura
 * própria de `main/DemoConsole` — sem rail, sem topbar, com identidade visual
 * deliberadamente diferente do console. O que este arquivo desenha é só o
 * conteúdo; a página em volta é do `DemoConsolePage`.
 *
 * Papel admin (o mesmo de `/comandos`): a chamada tem efeito no MCP server do
 * cliente — um DENY também é uma chamada que saiu.
 *
 * ## O histórico é local
 *
 * A conversa vive no estado da tela, e some ao sair. Não é omissão: o que ficou
 * gravado está na trilha (`agent.call` em T23) e no log do PDP (T16), e um
 * histórico próprio seria uma terceira cópia daquilo, envelhecendo sozinha.
 *
 * ## Dois transportes, uma tela
 *
 * O envio vai pelo canal `GET /api/v1/agents/conversation` e cai para as rotas REST
 * quando o upgrade não passa — quem decide é `useConversa`, e a tela não sabe por
 * onde a resposta veio. A diferença que sobra é **quem espera a decisão do PDP**:
 * pelo canal é o servidor (o veredito chega no evento `decisao`); pelo REST é
 * esta tela, consultando o log de 15 em 15 s. Manter os dois pollings ligados
 * faria a mesma decisão ser procurada duas vezes, e a linha piscaria entre a que
 * chegasse primeiro.
 */

/** Chave da tarefa no indicador de «Tarefas em execução». */
const SEND_TASK = 'agentes:chamada';

// A trilha responde duas perguntas — quem chamou quem, e qual foi a decisão — e o
// propósito diz as duas (decisão de 30/08, demo). A lista das seis etapas saiu
// junto com elas: prometê-las na tela e mostrar «não informado» em três é pior do
// que não prometê-las.
const proposito = () => t('conversation.page.purpose');

export function ConversePage() {
  const [pessoa, setPessoa] = useState<string | null>(null);
  const [agente, setAgente] = useState<string | null>(null);
  const [delegated, setDelegado] = useState<string | null>(null);
  const [tool, setTool] = useState<string | null>(null);

  const people = useLabPeople();
  // `com_contrato=true`: sem contrato ativo não há o que o PDP conceder, e a
  // chamada sairia só para tomar DENY por um motivo que não é o interessante.
  const identidades = useIdentities(true);
  // Faixa B (§5.6): o site do agente entra na opção do combobox. Só com mais de
  // um site (§3.1) — com um só, uma dica que diz sempre `default` em toda linha
  // é ruído.
  const multiSite = useMultiSite();
  const conversation = useConversation();
  const { manter, soltar } = useActionSlow();

  const identityBySlug = useMemo(() => {
    const mapping = new Map<string, string>();
    for (const identidade of identidades.data ?? []) {
      if (identidade.slug) mapping.set(identidade.slug, identidade.id);
    }
    return mapping;
  }, [identidades.data]);

  // O agente que **executa** a tool: o delegado, quando há delegação.
  const executorSlug = delegated ?? agente;
  const capabilities = useCapabilities(
    executorSlug ? (identityBySlug.get(executorSlug) ?? null) : null,
  );

  const personOptions: ComboOption[] = (people.data ?? []).map((p) => ({
    value: p.sub,
    label: `${p.sub} · ${p.profile}`,
    hint: `aud ${p.aud}`,
  }));

  const agentOptions: ComboOption[] = (identidades.data ?? [])
    .filter((identidade) => identidade.slug)
    .map((identidade) => ({
      value: identidade.slug as string,
      // O RÓTULO não muda: é o slug do agente, e é por ele que se procura e se
      // escolhe. O site entra como **dica**, nunca no rótulo (§5.6) — mexer no
      // rótulo mudaria o que a busca do combobox casa.
      label: identidade.slug as string,
      // Papel e site são os DOIS dados, juntos pela mesma pontuação que o
      // `personOptions` acima já usa. Não há chave de catálogo aqui de
      // propósito: `role` é vocabulário do tenant e `site_slug` é o slug (§3.2)
      // — os dois são DADO, e « · » é pontuação, não texto a traduzir. Uma
      // chave com `'{role} · {site}'` seria idêntica byte a byte nos dois
      // locales e nunca poderia divergir.
      //
      // `filter(Boolean)` porque as duas metades faltam em casos reais: uma
      // identidade descoberta sem atribuição não tem papel, e o site só entra
      // com `multi`. Sem nenhuma das duas, a dica volta a ser `undefined`, que
      // é o que o combobox espera para não reservar a linha.
      hint:
        [identidade.role, multiSite ? identidade.site_slug : null].filter(Boolean).join(' · ') ||
        undefined,
      mono: true,
    }));

  // «Pode invocar» do contrato vigente do agente escolhido. É ele — e não o nome
  // do papel — que decide se a delegação existe: `generalist` é um rótulo de
  // papel configurável por tenant, e o A2A aprovado é o fato.
  const agentCapabilities = useCapabilities(agente ? (identityBySlug.get(agente) ?? null) : null);
  const canInvoke = (agentCapabilities.data?.a2a_approved ?? [])
    .map((a) => a.slug)
    .filter((slug): slug is string => Boolean(slug));
  const isDelegador = canInvoke.length > 0;

  // O seletor lista o catálogo INTEIRO, sem marcação nenhuma (30/08, pedido do
  // mrolim): tool recém-dada por política ou contrato aparece sem recarregar a
  // página — e recarregar apaga a conversa. As da identidade entram na união
  // para nada sumir se o catálogo demorar a responder.
  const catalogo = useToolsCatalog();
  const tools = toolsOferecidas(capabilities.data ?? {});
  const toolNames = [...new Set([...tools.map((t) => t.nome), ...(catalogo.data ?? [])])].sort(
    (a, b) => a.localeCompare(b, 'pt-BR'),
  );
  const toolOptions: ComboOption[] = toolNames.map((nome) => ({
    value: nome,
    label: nome,
    mono: true,
  }));

  // «Delegar para» é **opcional**, mesmo para quem tem A2A: vazio é a chamada
  // direta. Exigi-lo tiraria do especialista que também delega a única forma de
  // chamar a própria tool — e o generalista sem tool própria já toma
  // `tool_fora_do_escopo_do_agente`, que é uma resposta e não um impedimento.
  const readyForSend = Boolean(pessoa && agente && tool);

  // O envio entra no indicador por `manter`/`soltar`, e não por `executar`: pelo
  // canal ele **não é uma promessa** — o pedido sai e a resposta volta em eventos,
  // como no discovery de 29/08. Um `executar` em volta de `conversa.enviar`
  // marcaria a tarefa como concluída no instante do envio.
  const enviando = useRef(false);
  useEffect(() => {
    if (conversation.enviando === enviando.current) return;
    enviando.current = conversation.enviando;
    if (!conversation.enviando) soltar(SEND_TASK);
  }, [conversation.enviando, soltar]);

  function send() {
    if (!readyForSend || !pessoa || !agente || !tool) return;
    // A tarefa é o **envio**, não a espera pela decisão: esta dura minutos, e
    // deixá-la no indicador diria «em execução» sobre uma chamada já respondida.
    manter(SEND_TASK, {
      titulo: t('conversation.page.task', { tool, agent: delegated ?? agente }),
    });
    conversation.enviar({
      pessoa,
      agente,
      delegado: delegated ?? '',
      tool,
      identityId: identityBySlug.get(delegated ?? agente) ?? null,
    });
  }

  const withoutIdentities = identidades.isSuccess && (identidades.data ?? []).length === 0;

  return (
    <>
      {/* O cabeçalho é da **ferramenta**, não da página: o `<h1>` («Console de
          demonstração») é da moldura, em `DemoConsolePage`. Aqui não entra o
          `PageHeader` do console — ícone em tijolo verde e título de 30px são
          moldura de produto, que é justamente o que esta tela deixou de ser
          (29/08). O que era o ⓘ do PageHeader vira texto: numa demonstração
          ninguém passa o mouse para descobrir o que a tela faz.

          `<div>`, e não `<header>`: `<header>` vira o landmark `banner`, e a
          página já tem o dela (o do Console de demonstração). Dois banners na
          mesma página dão ao leitor de tela dois cabeçalhos de página onde só
          existe um. */}
      <div className={s.cabecalho}>
        <p className={s.ferramenta}>{t('conversation.page.kind')}</p>
        <h2 className={s.titulo}>{t('conversation.page.title')}</h2>
        <p className={s.proposito}>{proposito()}</p>
        <p className={s.aviso}>{t('conversation.page.notice')}</p>
      </div>

      {people.isError ? (
        <ErrorBanner
          error={people.error}
          title={t('conversation.page.error.cast')}
          onRetry={() => void people.refetch()}
        />
      ) : null}

      {identidades.isError ? (
        <ErrorBanner
          error={identidades.error}
          title={t('conversation.page.error.identities')}
          onRetry={() => void identidades.refetch()}
        />
      ) : null}

      {withoutIdentities ? (
        <Alert tone="info" title={t('conversation.page.empty.identities')}>
          {t('conversation.page.empty.identities.help')}
        </Alert>
      ) : null}

      <div className={s.grade}>
        <Card title={t('conversation.form.title')} subtitle={t('conversation.form.subtitle')} fill>
          <FieldGrid>
            <Field
              label={t('conversation.form.person')}
              span={12}
              required
              tip={t('conversation.form.person.tip')}
            >
              {({ id, className: _classe, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('conversation.form.person')}
                  options={personOptions}
                  value={pessoa}
                  placeholder={t(
                    people.isLoading
                      ? 'conversation.form.person.loading'
                      : 'conversation.form.person.placeholder',
                  )}
                  emptyMessage={t('conversation.form.person.empty')}
                  onChange={setPessoa}
                />
              )}
            </Field>

            <Field
              label={t('conversation.form.agent')}
              span={12}
              required
              tip={t('conversation.form.agent.tip')}
            >
              {({ id, className: _classe, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('conversation.form.agent')}
                  options={agentOptions}
                  value={agente}
                  placeholder={t('conversation.form.agent.placeholder')}
                  emptyMessage={t('conversation.form.agent.empty')}
                  onChange={(valor) => {
                    setAgente(valor);
                    setDelegado(null);
                    setTool(null);
                  }}
                />
              )}
            </Field>

            {/* Só aparece para quem tem A2A aprovado — sem «Pode invocar» não há
                para quem delegar, e um campo vazio só faria perguntar. */}
            {isDelegador ? (
              <Field
                label={t('conversation.form.delegate')}
                span={12}
                tip={t('conversation.form.delegate.tip')}
              >
                {({ id, className: _classe, ...aria }) => (
                  <SearchCombobox
                    id={id}
                    {...aria}
                    label={t('conversation.form.delegate')}
                    options={canInvoke.map((slug) => ({ value: slug, label: slug, mono: true }))}
                    value={delegated}
                    clearable
                    placeholder={t('conversation.form.delegate.placeholder')}
                    emptyMessage={t('conversation.form.agent.empty')}
                    onChange={(valor) => {
                      setDelegado(valor);
                      setTool(null);
                    }}
                  />
                )}
              </Field>
            ) : null}

            <Field
              label={t('conversation.form.tool')}
              span={12}
              required
              tip={t('conversation.form.tool.tip')}
            >
              {({ id, className: _classe, ...aria }) => (
                <SearchCombobox
                  id={id}
                  {...aria}
                  label={t('conversation.form.tool')}
                  options={toolOptions}
                  value={tool}
                  disabled={!executorSlug}
                  placeholder={
                    executorSlug
                      ? capabilities.isLoading
                        ? t('conversation.form.tool.loading')
                        : t('conversation.form.tool.placeholder')
                      : t('conversation.form.tool.needsAgent')
                  }
                  emptyMessage={t('conversation.form.tool.empty')}
                  onChange={setTool}
                />
              )}
            </Field>
          </FieldGrid>

          <div className={s.acoes}>
            <LoadingButton loading={conversation.enviando} disabled={!readyForSend} onClick={send}>
              {t('conversation.form.send')}
            </LoadingButton>
          </div>

          <p className={s.nota}>{t('conversation.form.note')}</p>
        </Card>

        <Card title={t('conversation.swap.title')} fill>
          {conversation.error ? (
            <ErrorBanner
              error={conversation.error}
              title={t('conversation.page.error.send')}
              onRetry={conversation.limparErro}
              retryLabel={t('conversation.page.error.clear')}
            />
          ) : null}

          {conversation.trocas.length === 0 && !conversation.enviando ? (
            <p className={s.vazio}>{t('conversation.swap.empty')}</p>
          ) : null}

          <div className={s.conversa}>
            {conversation.trocas.map((swap) => (
              <SwapWithDecision
                key={swap.chave}
                troca={swap}
                // Só o caminho REST procura a decisão daqui: pelo canal quem
                // espera é o servidor, e ela já chega dentro da troca.
                procurarNoLog={conversation.transporte === 'rest'}
              />
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}

/**
 * Uma troca, com o polling da decisão dela quando o transporte é REST.
 *
 * O hook do polling vive **aqui**, e não na página, porque cada troca procura a
 * sua decisão: um hook só na página acompanharia apenas a última, e as anteriores
 * ficariam paradas em «aguardando o log…» para sempre — exatamente quando o
 * veredito delas acabou de chegar.
 *
 * `procurarNoLog` falso passa `null` ao hook e ele não consulta nada: pelo canal
 * a decisão já vem no `troca.decisao`, e procurá-la também aqui consultaria o log
 * duas vezes pela mesma linha.
 */
function SwapWithDecision({
  troca,
  procurarNoLog,
}: {
  troca: SwapInScreen;
  procurarNoLog: boolean;
}) {
  const doLog = useCallDecision(
    procurarNoLog && troca.resposta
      ? {
          chave: troca.chave,
          aplicacao: swapExecutor(troca),
          tool: troca.tool,
          // `pessoa` e `statusHttp` entram no casamento desde 30/08: sem eles a
          // decisão de outra pessoa, na mesma tool, virava o veredito desta
          // chamada — ver `decisaoDaChamada`.
          pessoa: troca.pessoa,
          statusHttp: troca.resposta.statusHttp,
          desde: troca.desde,
        }
      : null,
  );

  return (
    <Swap
      troca={troca}
      decisao={procurarNoLog ? doLog : troca.decisao}
      identityIdParaSimulador={troca.identityId}
    />
  );
}
